import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigurationPageHeader } from '@modules/configuration/components/ConfigurationPageHeader';
import {
  configurationPageClass,
  configurationPageContentClass,
} from '@modules/configuration/components/configurationLayout';
import { UserModel } from '../../models/User';
import { FiltersSection, InviteUserButton } from '../interaction';
import { UsersEmptyState } from './UsersEmptyState';
import { UsersList } from './UsersList';

interface UsersViewProps {
  users: UserModel[];
  allUsersCount: number;
  rolesMap: Record<number, string>;
  searchTerm: string;
  isFetching?: boolean;
  isFiltering?: boolean;
  onSearchChange: (value: string) => void;
  onRefetch: () => void;
}

export function UsersView({
  users,
  allUsersCount,
  rolesMap,
  searchTerm,
  isFetching = false,
  isFiltering = false,
  onSearchChange,
  onRefetch,
}: UsersViewProps) {
  const { t } = useTranslation('usersModule');
  const [searchInitialValue] = useState(() => searchTerm);

  const hasUsers = allUsersCount > 0;
  const hasFilteredResults = users.length > 0;
  const isSearchWithNoResults = hasUsers && !hasFilteredResults && searchTerm.trim().length > 0;
  const showingCount = users.length;
  const isFiltered = searchTerm.trim().length > 0;
  const isBusy = isFetching || isFiltering;

  return (
    <div
      className={`${configurationPageClass} pb-6 transition-opacity duration-300 ${isBusy ? 'pointer-events-none opacity-90' : ''}`}
      data-testid="users-view"
    >
      <div className="shrink-0">
        <ConfigurationPageHeader
          title={t('pageTitle')}
          description={t('pageDescription')}
          action={hasUsers ? <InviteUserButton onSuccess={onRefetch} /> : undefined}
        />

        {hasUsers && (
          <div className="mt-8 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <FiltersSection initialValue={searchInitialValue} onSearchChange={onSearchChange} />
            <p className="shrink-0 text-xs text-muted-foreground sm:text-sm">
              {isFiltered
                ? t('list.countFiltered', { count: showingCount, total: allUsersCount })
                : t('list.count', { count: allUsersCount })}
            </p>
          </div>
        )}
      </div>

      <div className={`${configurationPageContentClass} flex flex-col overflow-hidden`}>
        {!hasUsers ? (
          <UsersEmptyState onInviteSuccess={onRefetch} />
        ) : isSearchWithNoResults ? (
          <div
            className="rounded-lg border border-dashed border-border bg-muted/20 px-6 py-10 text-center"
            data-testid="users-no-results"
          >
            <p className="text-sm font-medium text-foreground">{t('emptyState.noResultsTitle')}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t('emptyState.noResults')}</p>
          </div>
        ) : (
          <UsersList users={users} rolesMap={rolesMap} onRefetch={onRefetch} />
        )}
      </div>
    </div>
  );
}
